import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MenuItem,
  makeStyles,
  MenuItemLink,
  MenuItemRadio,
  MenuItemSwitch,
  mergeClasses,
  MenuGroup,
  MenuGroupHeader,
  MenuDivider
} from "@fluentui/react-components";
import { MoreVertical24Filled } from "@fluentui/react-icons";

export const MORE_MENU_POSITIONING = {
  position: "above",
  align: "end",
  offset: { mainAxis: 15 },
  overflowBoundary: "window",
  overflowBoundaryPadding: {
    top: 8,
    bottom: 72,
    start: 8,
    end: 8
  },
  fallbackPositions: ["above-start", "below-end", "below-start"],
  autoSize: "height"
};

export const MORE_MENU_SUBMENU_POSITIONING = {
  position: "after",
  align: "top",
  offset: { mainAxis: 4 },
  overflowBoundary: "window",
  overflowBoundaryPadding: 8,
  fallbackPositions: ["before-top", "above-end", "below-end"],
  autoSize: "height"
};

function mergePositioningRef(positioning, positioningRef) {
  if (typeof positioning === "string") {
    return positioning;
  }

  return {
    ...positioning,
    positioningRef
  };
}

const useClasses = makeStyles({
  menuListItem: {
    minWidth: "200px",
    "&:hover": {
      backgroundColor: "var(--accent1-color-hover)"
    },
    "& > * ": {
      color: "var(--color-text-primary)"
    },
    "&:hover > * ": {
      color: "inherit!important"
    },
    maxWidth: "unset"
  },
  switchSubmenuItem: {
    alignItems: "center"
  },
  radioSubmenuItem: {
    "& .fui-MenuItemRadio__checkmark": {
      marginTop: 0
    }
  },
  menuPopover: {
    minWidth: "240px",
    maxWidth: "min(320px, calc(100vw - 16px))"
  },
  buttonPressed: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  overlayDrawer: {
    height: "100%"
  }
});

export function ToolbarMoreButton({ styles, items, children }) {
  const classes = useClasses();

  const [open, setOpen] = useState(false);
  const rootPositioningRef = useRef(null);
  const submenuPositioningRefs = useRef(new Map());

  const getSubmenuPositioningRef = useCallback(key => {
    if (!submenuPositioningRefs.current.has(key)) {
      submenuPositioningRefs.current.set(key, React.createRef());
    }

    return submenuPositioningRefs.current.get(key);
  }, []);

  const updateSubmenuPositions = useCallback(() => {
    submenuPositioningRefs.current.forEach(positioningRef => {
      positioningRef.current?.updatePosition?.();
    });
  }, []);

  const updateMenuPositions = useCallback(() => {
    rootPositioningRef.current?.updatePosition?.();
    updateSubmenuPositions();
  }, [updateSubmenuPositions]);

  const rootPositioning = useMemo(
    () => ({
      ...MORE_MENU_POSITIONING,
      positioningRef: rootPositioningRef,
      onPositioningEnd: updateSubmenuPositions
    }),
    [updateSubmenuPositions]
  );

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    updateMenuPositions();

    const targetWindow = typeof window !== "undefined" ? window : undefined;

    if (targetWindow?.requestAnimationFrame) {
      const animationFrame = targetWindow.requestAnimationFrame(updateMenuPositions);

      return () => targetWindow.cancelAnimationFrame(animationFrame);
    }

    const timeout = setTimeout(updateMenuPositions, 0);

    return () => clearTimeout(timeout);
  }, [items, open, updateMenuPositions]);

  const rootCheckedValues = items.reduce(
    (checkedValues, group) => ({
      ...checkedValues,
      ...(group.checkedValues || {})
    }),
    {}
  );
  const handleRootCheckedValueChange = (_, data) => {
    const ownerGroup = items.find(
      group => group.onCheckedValueChange && group.checkedValues && data.name in group.checkedValues
    );

    if (ownerGroup) {
      ownerGroup.onCheckedValueChange(_, data);
    }
  };
  const hasRootCheckedValues = Object.keys(rootCheckedValues).length > 0;

  const handleItemClick = (item, event) => {
    if (item.unavailable) {
      if (item.onClick && typeof item.onClick === "function") {
        item.onClick(event);
      }
      return;
    }

    if (item.closeMenuOnClick !== false) {
      setOpen(false);
    }

    if (item.onClick && typeof item.onClick === "function") {
      item.onClick(event);
    }
  };

  return (
    <>
      <Menu
        positioning={rootPositioning}
        onOpenChange={(_, data) => setOpen(data.open)}
        open={open}
        persistOnItemClick
        checkedValues={hasRootCheckedValues ? rootCheckedValues : undefined}
        onCheckedValueChange={hasRootCheckedValues ? handleRootCheckedValueChange : undefined}
      >
        <MenuTrigger disableButtonEnhancement>
          <MenuButton
            appearance="subtle"
            className={open ? mergeClasses(styles.button, classes.buttonPressed) : styles.button}
            icon={<MoreVertical24Filled />}
          />
        </MenuTrigger>
        <MenuPopover className={classes.menuPopover}>
          <MenuList>
            {items.map((group, groupIndex) => (
              <React.Fragment key={`group-${groupIndex}`}>
                <MenuGroup>
                  <MenuGroupHeader>{group.title}</MenuGroupHeader>
                  {group.items.map((item, itemIndex) => {
                    const itemKey = item.id || `menu-item-${groupIndex}-${itemIndex}`;

                    return item.href ? (
                      <MenuItemLink
                        className={classes.menuListItem}
                        key={itemKey}
                        icon={item.icon}
                        onClick={event => handleItemClick(item, event)}
                        href={item.href}
                        target={item.target || "_self"}
                        secondaryContent={item.secondaryContent}
                      >
                        {item.label}
                      </MenuItemLink>
                    ) : item.type === "switch" ? (
                      <MenuItemSwitch
                        className={mergeClasses(
                          classes.menuListItem,
                          classes.switchSubmenuItem,
                          item.className || undefined
                        )}
                        key={itemKey}
                        icon={item.icon}
                        onClick={event => handleItemClick(item, event)}
                        disabled={item.disabled}
                        aria-disabled={item.unavailable || item.disabled}
                        secondaryContent={item.secondaryContent}
                        name={item.name}
                        value={item.value}
                      >
                        {item.label}
                      </MenuItemSwitch>
                    ) : item.type === "radio" ? (
                      <MenuItemRadio
                        className={mergeClasses(
                          classes.menuListItem,
                          classes.radioSubmenuItem,
                          item.className || undefined
                        )}
                        key={itemKey}
                        icon={item.icon}
                        onClick={event => handleItemClick(item, event)}
                        disabled={item.disabled}
                        aria-disabled={item.unavailable || item.disabled}
                        secondaryContent={item.secondaryContent}
                        name={item.name}
                        value={item.value}
                      >
                        {item.label}
                      </MenuItemRadio>
                    ) : item.submenu ? (
                      <Menu
                        key={itemKey}
                        positioning={mergePositioningRef(
                          item.positioning || MORE_MENU_SUBMENU_POSITIONING,
                          getSubmenuPositioningRef(itemKey)
                        )}
                        persistOnItemClick
                        checkedValues={item.submenuCheckedValues}
                        onCheckedValueChange={item.onSubmenuCheckedValueChange}
                      >
                        <MenuTrigger disableButtonEnhancement>
                          <MenuItem
                            className={classes.menuListItem}
                            icon={item.icon}
                            aria-disabled={item.unavailable}
                            secondaryContent={item.secondaryContent}
                          >
                            {item.label}
                          </MenuItem>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            {item.submenu.map(subItem =>
                              subItem.href ? (
                                <MenuItemLink
                                  className={mergeClasses(classes.menuListItem, subItem.className || undefined)}
                                  key={subItem.id || `submenu-item-${subItem.label}`}
                                  icon={subItem.icon}
                                  onClick={event => handleItemClick(subItem, event)}
                                  href={subItem.href}
                                  target={subItem.target || "_self"}
                                  secondaryContent={subItem.secondaryContent}
                                >
                                  {subItem.label}
                                </MenuItemLink>
                              ) : subItem.type === "switch" ? (
                                <MenuItemSwitch
                                  className={mergeClasses(
                                    classes.menuListItem,
                                    classes.switchSubmenuItem,
                                    subItem.className || undefined
                                  )}
                                  key={subItem.id || `submenu-item-${subItem.label}`}
                                  icon={subItem.icon}
                                  onClick={event => handleItemClick(subItem, event)}
                                  disabled={subItem.disabled}
                                  aria-disabled={subItem.unavailable || subItem.disabled}
                                  secondaryContent={subItem.secondaryContent}
                                  name={subItem.name}
                                  value={subItem.value}
                                >
                                  {subItem.label}
                                </MenuItemSwitch>
                              ) : subItem.type === "radio" ? (
                                <MenuItemRadio
                                  className={mergeClasses(
                                    classes.menuListItem,
                                    classes.radioSubmenuItem,
                                    subItem.className || undefined
                                  )}
                                  key={subItem.id || `submenu-item-${subItem.label}`}
                                  icon={subItem.icon}
                                  onClick={event => handleItemClick(subItem, event)}
                                  disabled={subItem.disabled}
                                  aria-disabled={subItem.unavailable || subItem.disabled}
                                  secondaryContent={subItem.secondaryContent}
                                  name={subItem.name}
                                  value={subItem.value}
                                >
                                  {subItem.label}
                                </MenuItemRadio>
                              ) : (
                                <MenuItem
                                  className={mergeClasses(classes.menuListItem, subItem.className || undefined)}
                                  key={subItem.id || `submenu-item-${subItem.label}`}
                                  icon={subItem.icon}
                                  onClick={event => handleItemClick(subItem, event)}
                                  disabled={subItem.disabled}
                                  aria-disabled={subItem.unavailable || subItem.disabled}
                                  secondaryContent={subItem.secondaryContent}
                                >
                                  {subItem.label}
                                </MenuItem>
                              )
                            )}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    ) : (
                      <MenuItem
                        className={mergeClasses(classes.menuListItem, item.className || undefined)}
                        key={itemKey}
                        icon={item.icon}
                        onClick={event => handleItemClick(item, event)}
                        disabled={item.disabled}
                        aria-disabled={item.unavailable || item.disabled}
                        secondaryContent={item.secondaryContent}
                      >
                        {item.label}
                      </MenuItem>
                    );
                  })}
                </MenuGroup>
                {groupIndex < items.length - 1 && <MenuDivider key={`divider-${groupIndex}`} />}
              </React.Fragment>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
      {children}
    </>
  );
}

// Update propTypes as necessary
ToolbarMoreButton.propTypes = {
  styles: PropTypes.object,
  items: PropTypes.array,
  children: PropTypes.node
};
